import { customerLabel } from './customerName';
import i18n from '../../../i18n';
import { CURRENT_SELECTION_GROUP_ID, legacyCurrentSelectionIds } from '../../../lib/clientConfig';
import type { FleetRestaurant, ScopeGroupSummary } from '../types';

/**
 * The venue part of the view context (`regionFilter`, the Topbar's `s=`):
 *
 * - `all` (or null): the restaurant opened, alone. The default (decision 7.7.1).
 * - `outlet:<id>`: one venue.
 * - `org`: "All venues of <group>": the opened venue's group, the venues of
 *   it the person can open (resolved on the server).
 * - `group:<id>`: one of the person's own groups, kept on the server.
 * - `venues:<id>,<id>`: a set of venues (the multi-select's Apply, and a
 *   shared link's venues for its recipient), sorted and unique.
 */

export const ORG_FILTER = 'org';
export const VENUES_FILTER_PREFIX = 'venues:';
/** The most venues a `venues:` filter names (the server's VENUES_FILTER_MAX). */
export const VENUES_FILTER_MAX = 500;

export function scopeGroupFilterKey(groupId: string): string {
  return `group:${groupId}`;
}

export function isScopeGroupFilter(filter: string | null | undefined): boolean {
  return !!filter?.startsWith('group:');
}

export function scopeGroupIdFromFilter(
  filter: string | null | undefined,
): string | null {
  if (!filter?.startsWith('group:')) {
    return null;
  }
  return filter.slice(6) || null;
}

export function outletFilterKey(outletId: string): string {
  return `outlet:${outletId}`;
}

export function isOutletFilter(filter: string | null | undefined): boolean {
  return !!filter?.startsWith('outlet:');
}

export function outletIdFromFilter(
  filter: string | null | undefined,
): string | null {
  if (!filter?.startsWith('outlet:')) {
    return null;
  }
  return filter.slice(7) || null;
}

export function isOrgFilter(filter: string | null | undefined): boolean {
  return filter === ORG_FILTER;
}

/** A set of venues as one filter: sorted, unique, at most VENUES_FILTER_MAX. */
export function venuesFilterKey(ids: Iterable<string>): string {
  const unique = [...new Set([...ids].filter(id => id && !id.includes(',')))].sort();
  return `${VENUES_FILTER_PREFIX}${unique.slice(0, VENUES_FILTER_MAX).join(',')}`;
}

export function isVenuesFilter(filter: string | null | undefined): boolean {
  return !!filter?.startsWith(VENUES_FILTER_PREFIX);
}

export function venueIdsFromFilter(filter: string | null | undefined): string[] {
  if (!isVenuesFilter(filter)) return [];
  return [
    ...new Set(
      (filter as string)
        .slice(VENUES_FILTER_PREFIX.length)
        .split(',')
        .map(id => id.trim())
        .filter(Boolean),
    ),
  ].slice(0, VENUES_FILTER_MAX);
}

/**
 * The venues a filter names, as one filter: one venue as `outlet:`, several
 * as `venues:`. Null for none.
 */
export function filterForVenues(ids: Iterable<string>): string | null {
  const unique = [...new Set([...ids].filter(Boolean))];
  if (!unique.length) return null;
  if (unique.length === 1) return outletFilterKey(unique[0]!);
  return venuesFilterKey(unique);
}

/**
 * A saved scope or link naming the multi-select's former scratch group
 * (`group:current-selection`) reads as the venues it held in this browser,
 * if it still holds them; otherwise as itself (which then resolves to the
 * opened venue).
 */
export function migrateCurrentSelectionFilter(filter: string | null | undefined): string | null {
  if (scopeGroupIdFromFilter(filter) !== CURRENT_SELECTION_GROUP_ID) return filter ?? null;
  return filterForVenues(legacyCurrentSelectionIds()) ?? 'all';
}

/** Restaurant ids implied by the active regionFilter (for draft multi-select seed). */
export function restaurantIdsForScopeFilter(
  filter: string | null | undefined,
  groups: { id: string; restaurantIds: string[] }[],
  activeRestaurantId?: string | null,
  orgVenueIds?: string[] | null,
): string[] {
  const outletId = outletIdFromFilter(filter);
  if (outletId) return [outletId];
  if (isVenuesFilter(filter)) return venueIdsFromFilter(filter);
  if (isOrgFilter(filter)) {
    if (orgVenueIds?.length) return [...orgVenueIds];
    return activeRestaurantId ? [activeRestaurantId] : [];
  }
  const groupId = scopeGroupIdFromFilter(filter);
  if (groupId) {
    const g = groups.find(x => x.id === groupId);
    if (g?.restaurantIds?.length) return [...g.restaurantIds];
    // Do not pretend the home restaurant is this group. That painted Das Bräu
    // as the 1-outlet scope while the URL still had group:l'osteria.
    return [];
  }
  if (activeRestaurantId) return [activeRestaurantId];
  return [];
}

/**
 * Restaurants in the operational scope — chip + AI chrome.
 * Never use a view payload length (Live/Performance/Settings arrays go 227→1→0
 * while loading or when the home restaurant sits outside the group).
 */
export function scopeOutletMembershipCount(input: {
  regionFilter: string | null | undefined;
  groups: { id: string; restaurantIds: string[] }[];
  groupSummaries?: { id: string; count: number }[];
  scopedOutletCount?: number | null;
  activeRestaurantId?: string | null;
  orgVenueIds?: string[] | null;
}): number {
  const outletId = outletIdFromFilter(input.regionFilter);
  if (outletId) return 1;
  if (isVenuesFilter(input.regionFilter)) return venueIdsFromFilter(input.regionFilter).length;
  if (isOrgFilter(input.regionFilter)) {
    if (input.orgVenueIds?.length) return input.orgVenueIds.length;
    if ((input.scopedOutletCount ?? 0) > 0) return input.scopedOutletCount!;
    return input.activeRestaurantId ? 1 : 0;
  }

  const groupId = scopeGroupIdFromFilter(input.regionFilter);
  if (groupId) {
    const ids = restaurantIdsForScopeFilter(
      input.regionFilter,
      input.groups,
      null,
    );
    if (ids.length) return ids.length;
    const summary = input.groupSummaries?.find(g => g.id === groupId);
    if (summary && summary.count > 0) return summary.count;
    // keepPreviousData can still be the home restaurant's 1 while the group
    // getScope is in flight — do not paint that as the group size.
    if ((input.scopedOutletCount ?? 0) > 1) return input.scopedOutletCount!;
    return 0;
  }

  if ((input.scopedOutletCount ?? 0) > 0) return input.scopedOutletCount!;
  return input.activeRestaurantId ? 1 : 0;
}

/**
 * CURRENT pill on a restaurant row: the single outlet in scope, or the home
 * restaurant when the filter is all. A selected group is the scope — do not
 * tag the URL/home restaurant (it may not even be a member).
 */
export function isCurrentScopeRestaurant(
  id: string,
  regionFilter: string | null | undefined,
  activeRestaurantId?: string | null,
): boolean {
  const outletId = outletIdFromFilter(regionFilter);
  if (outletId) return outletId === id;
  if (scopeGroupIdFromFilter(regionFilter) || isVenuesFilter(regionFilter) || isOrgFilter(regionFilter)) {
    return false;
  }
  return Boolean(activeRestaurantId) && id === activeRestaurantId;
}

export function restaurantIdSetsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const id of a) {
    if (!b.has(id)) return false;
  }
  return true;
}

// The person's groups as the server last listed them (getScopeConfig), so
// cache keys can follow a group whose members changed under the same id.
let knownGroups: { id: string; restaurantIds: string[] }[] = [];

/** Remember the person's groups (OpsCommand sets them when getScopeConfig loads). */
export function setKnownScopeGroups(groups: { id: string; restaurantIds: string[] }[]): void {
  knownGroups = groups.map(g => ({ id: g.id, restaurantIds: [...g.restaurantIds] }));
}

/**
 * SWR cache discriminator for a `group:` filter. The filter key is stable
 * while the group's members change (an edit), so without this an edit could
 * reuse the previous members' response.
 */
export function scopeMembershipFingerprint(
  filter: string | null | undefined,
): string | null {
  const groupId = scopeGroupIdFromFilter(filter);
  if (!groupId) return null;
  const ids = knownGroups.find(g => g.id === groupId)?.restaurantIds ?? [];
  return ids.slice().sort().join(',') || null;
}

/** "All venues of ButchersTable" (or of the group, when aleno has no name for it). */
export function orgFilterLabel(orgName?: string | null): string {
  const customer = customerLabel(orgName);
  return customer
    ? i18n.t('chrome:scope.allVenuesOf', { name: customer })
    : i18n.t('chrome:scope.allVenuesOfGroup');
}

export function scopeFilterLabel(
  filter: string | null | undefined,
  scopeGroups: ScopeGroupSummary[],
  fleetRestaurants?: FleetRestaurant[],
  allLabel: string = i18n.t('chrome:filter.allOutlets'),
  orgName?: string | null,
): string {
  if (!filter || filter === 'all') {
    return allLabel;
  }
  if (isOrgFilter(filter)) return orgFilterLabel(orgName);
  if (isVenuesFilter(filter)) {
    const ids = venueIdsFromFilter(filter);
    const names = ids.map(id => fleetRestaurants?.find(r => r.id === id)?.name ?? id);
    if (names.length === 1) return names[0]!;
    if (names.length === 2) return `${names[0]} · ${names[1]}`;
    return i18n.t('chrome:scope.nVenues', { count: names.length });
  }
  const groupId = scopeGroupIdFromFilter(filter);
  if (groupId) {
    return scopeGroups.find(g => g.id === groupId)?.name ?? i18n.t('chrome:filter.customGroup');
  }
  const oId = outletIdFromFilter(filter);
  if (oId) {
    return fleetRestaurants?.find(r => r.id === oId)?.name ?? i18n.t('chrome:filter.singleOutlet');
  }
  return filter;
}

export function mappedScopeProposal(input: {
  mappedIds: string[];
  scopedIds: string[];
}): {
  newlyMappedIds: string[];
  alreadyInScopeIds: string[];
} {
  const mapped = [...new Set(input.mappedIds.filter(Boolean))];
  const scoped = new Set(input.scopedIds.filter(Boolean));
  return {
    newlyMappedIds: mapped.filter(id => !scoped.has(id)),
    alreadyInScopeIds: mapped.filter(id => scoped.has(id)),
  };
}

/** Union mapped restaurants into the active dashboard scope (Topbar Apply path). */
export function expandScopeWithMappedRestaurants(input: {
  mappedIds: string[];
  regionFilter: string | null;
  groups: { id: string; restaurantIds: string[] }[];
  activeRestaurantId: string;
  orgVenueIds?: string[] | null;
}): { regionFilter: string; switchRestaurantId?: string } | null {
  const mapped = [...new Set(input.mappedIds.filter(Boolean))];
  if (!mapped.length) return null;
  const current = restaurantIdsForScopeFilter(
    input.regionFilter,
    input.groups,
    input.activeRestaurantId,
    input.orgVenueIds,
  );
  const union = [...new Set([...current, ...mapped])];
  if (restaurantIdSetsEqual(new Set(union), new Set(current))) return null;
  if (union.length === 1) {
    const id = union[0]!;
    return { regionFilter: outletFilterKey(id), switchRestaurantId: id };
  }
  return {
    regionFilter: venuesFilterKey(union),
    switchRestaurantId: current.includes(input.activeRestaurantId)
      ? undefined
      : union[0],
  };
}
